/**
 * Minimal async SQL interface over SQLite. The same engine code runs on:
 * - the app (browser, phone, Windows): SQLite WASM (OPFS) in a Web Worker,
 * - tests and Node tools: node:sqlite.
 */
export type SqlValue = string | number | null;

export type SqlStatement = readonly [sql: string, params: readonly SqlValue[]];

export interface SqlExecutor {
  run(sql: string, params?: readonly SqlValue[]): Promise<void>;
  all<T = Record<string, SqlValue>>(sql: string, params?: readonly SqlValue[]): Promise<T[]>;
  /** Writes in order, as run() one after another would, but in one go (one message to a worker). */
  runMany(statements: readonly SqlStatement[]): Promise<void>;
}

export interface SqlDriver extends SqlExecutor {
  /**
   * Runs `fn` atomically. Inside, use only the given executor. Transactions and top-level
   * statements are serialised, so a screen writing while sync runs cannot end up inside
   * (and be rolled back with) someone else's transaction.
   */
  transaction<T>(fn: (tx: SqlExecutor) => Promise<T>): Promise<T>;
}

/** Builds a serialised SqlDriver from raw single-connection primitives. */
export function createSerialDriver(raw: SqlExecutor & { exec(sql: string): Promise<void> }): SqlDriver {
  let tail: Promise<unknown> = Promise.resolve();
  const enqueue = <T>(task: () => Promise<T>): Promise<T> => {
    const next = tail.then(task, task);
    tail = next.catch(() => undefined);
    return next;
  };
  return {
    run: (sql, params) => enqueue(() => raw.run(sql, params)),
    all: <T>(sql: string, params?: readonly SqlValue[]) => enqueue(() => raw.all<T>(sql, params)),
    runMany: (statements) => enqueue(() => raw.runMany(statements)),
    transaction: <T>(fn: (tx: SqlExecutor) => Promise<T>) =>
      enqueue(async () => {
        await raw.exec('begin');
        try {
          const result = await fn(raw);
          await raw.exec('commit');
          return result;
        } catch (error) {
          await raw.exec('rollback');
          throw error;
        }
      }),
  };
}
